<script setup lang="ts">
// @ts-expect-error raison: lib externe mal typée
import { onMounted, ref, defineProps } from 'vue';
import { formatAmount } from '@/utils';
import axios from 'axios';
import moment from 'moment';
import CustomWidget from '@/components/CustomWidget.vue';

interface DecaissementMensuel {
    id: number;
    mois: number;
    date_prevision: string | null;
    date_operation: string | null;
    montant: number;
    percent: number;
    statut: 'pending' | 'done' | 'cancelled';
    countdown: string | null;
}

const props = defineProps<{
    userId: number
}>();
const decaissementsMensuels = ref<DecaissementMensuel[]>([]);
const decaissementsStats = ref({
    debloque: 0,
    debloque_percent: 0,
    bloque: 0,
    bloque_percent: 0,
    total: 0,
});
const decaissementsLoading = ref(false);
const minimized = ref(true);

onMounted(() => {
    loadDecaissementsMensuels();
});

async function loadDecaissementsMensuels() {
    decaissementsLoading.value = true;
    try {
        const response = await axios.get(`/backoffice/users/${props.userId}/decaissements_mensuels`);
        decaissementsMensuels.value = response.data.items || [];
        decaissementsStats.value = response.data.stats || {
            debloque: 0, debloque_percent: 0,
            bloque: 0, bloque_percent: 0,
            total: 0,
        };
    } catch (error: any) {
        console.error('Erreur chargement décaissements mensuels:', error);
    } finally {
        decaissementsLoading.value = false;
    }
}

function formatPrelevementDateLong(dateString: string | null): string {
    if (!dateString) return '-';
    const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    const date = moment(dateString);
    if (!date.isValid()) return '-';
    return `${date.format('DD')} ${monthsFr[date.month()]} ${date.year()}`;
}

function formatPrelevementDate(dateString: string | null): string {
    if (!dateString) return '-';
    const monthsFr = ['Jan', 'Fév', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
    const date = moment(dateString);
    if (!date.isValid()) return '-';
    return `${date.format('DD')} ${monthsFr[date.month()]} ${date.year()}`;
}
</script>
<style lang="css" scoped>
.grid-minized {
    height: 380px;
    overflow: hidden;
}
</style>
<template>
    <custom-widget title="Décaissement mensuel" :items="decaissementsMensuels" :loading="decaissementsLoading">
        <template #action>
            <button class="btn lg py-3 white2 active">
                <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                <span>Revolving</span>
            </button>
        </template>
        <template #header>
            <div class="flex items-center gap-6">
                <div class="s7">
                    <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                        <div class="circle-medium bg-color-1000"></div>
                        Débloqué
                    </h5>
                    <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmount(decaissementsStats.debloque) }} F</h1>
                    <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ decaissementsStats.debloque_percent }}%</h5>
                    <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" :style="`width: ${decaissementsStats.debloque_percent}%;`">
                            {{ decaissementsStats.debloque_percent }}%
                        </div>
                    </div>
                </div>
                <div class="s7">
                    <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                        <div class="circle-medium bg-purple"></div>
                        Bloqué
                    </h5>
                    <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmount(decaissementsStats.bloque) }} F</h1>
                    <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ decaissementsStats.bloque_percent }}%</h5>
                    <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-purple text-white" :style="`width: ${decaissementsStats.bloque_percent}%;`">
                            {{ decaissementsStats.bloque_percent }}%
                        </div>
                    </div>
                </div>
                <div class="s6">
                    <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                        <div class="circle-medium bg-green-light"></div>
                        Total
                    </h5>
                    <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmount(decaissementsStats.total) }} F</h1>
                    <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                    <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                            100%
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #item="{ item }">
            <div class="widget border border-3 shadow-200 rounded-30 gap-5 px-7 py-5" :class="{ active: item.statut === 'done', 'opacity-6': item.statut === 'cancelled' }">
                <div class="s10">
                    <h6 class="secondary-font font-semibold mb-1">{{ formatPrelevementDateLong(item.date_prevision) }}</h6>
                    <h3 class="secondary-font font-semibold text-grey-900 py-1" style="white-space: nowrap;">{{ formatAmount(item.montant) }}&nbsp;F</h3>
                    <p class="secondary-font font-semibold text-purple">{{ item.percent }}%</p>
                </div>
                <div class="s10 text-right">
                    <h5 v-if="item.statut === 'pending' && item.countdown" class="secondary-font font-semibold text-primary-color pb-1">{{ item.countdown }}</h5>
                    <div v-if="item.statut === 'done'" class="mb-4">
                        <span class="tag-state notif stroke">Effectué</span>
                    </div>
                    <div v-else-if="item.statut === 'cancelled'" class="mb-4">
                        <span class="tag-state danger stroke">Annulé</span>
                    </div>
                    <p v-if="item.statut === 'done' && item.date_operation" class="secondary-font font-semibold text-grey-600 pt-1">Date de déblocage</p>
                    <h6 v-if="item.statut === 'done' && item.date_operation" class="secondary-font font-semibold text-grey-600 py-1">{{ formatPrelevementDate(item.date_operation) }}</h6>
                </div>
            </div>
        </template>
    </custom-widget>
</template>